/* eslint-disable */

import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ECharts from "echarts-for-react";
import { Box, Chip, Tab, Tabs } from "@mui/material";

import MainCard from "components/MainCard";
import useConfig from "hooks/useConfig";

import {
    getEquipmentMeasurementLogsStatsDurationQueryString,
    getEquipmentOpLogsStatsOpStatusDurationQueryString,
} from "components/axios/equipmentAxios";

import {
    buildOption,
    buildTimeAxis,
    getCurrentPeriodLabel,
    getDurationUnit,
    mapDurationToAxis
} from "data/dateUtils";

import {
    buildDetailDurationGroups,
    buildDetailDurationSeries
} from "./operationDurationUtils";
import CommonExcelDownload from "components/CommonExcelDownload";

const DELAY_TIME =
    Number(process.env.REACT_APP_DELAY_TIME) || 60000;

const cellStyle = {
    padding: "6px 10px",
    border: "1px solid #444",
    textAlign: "center"
};

function EquipmentDurationGraphs({
    equipment,
    equipmentId,
    height = 200,
    target = "dashboard",
}) {
    const { t } = useTranslation();
    const { mode } = useConfig();

    const textColor =
        mode === "light" ? "#262626" : "#ffffff";
    const bgColor =
        mode === "light" ? "#f9f9fa" : "#2f2f2f";

    // 기간: DAY / MONTH / YEAR
    const [tab, setTab] = useState("DAY");

    // 조회 유형: TOTAL / OPERATION / PART
    const [viewType, setViewType] = useState("TOTAL");

    const isOperation = true;
    // const isOperation = ((equipment.equipmentCtgr.parent.parent.code === 'EV' || equipment.equipmentCtgr.parent.parent.code === 'ES' 
    //             || equipment.equipmentCtgr.code.startsWith('DOUBLE.') || equipment.equipmentCtgr.code.startsWith('SINGLE.')
    //             || equipment.equipmentCtgr.code.startsWith('GEARED.') || equipment.equipmentCtgr.code.startsWith('GEARLESS.')
    //             || equipment.equipmentCtgr.code.startsWith('DBERAIL.')));
    // console.log(isOperation);

    const [totalDurationSeries, setTotalDurationSeries] = useState([]);
    const [detailRawData, setDetailRawData] = useState([]);
    const [selectedItemKey, setSelectedItemKey] = useState(null);
    const [detailGroups, setDetailGroups] = useState([]);
    const [axis, setAxis] = useState([]);

    const chartRef = useRef(null);
    
    const graphParam = `?equipment.id=${equipmentId}`;

    const showCurrentPeriod =
        viewType !== "TOTAL" && selectedItemKey === null;

    // const BASE_OPTION = {
    //     tooltip: { trigger: "axis" },
    //     grid: {
    //         left: 40,
    //         right: 20,
    //         top: 40,
    //         bottom: 30,
    //     },
    //     xAxis: {
    //         type: "category",
    //         data: [],
    //     },
    //     yAxis: {
    //         type: "value",
    //     },
    //     series: [],
    // };

    useEffect(() => {
        if (!equipmentId) {
            setAxis([]);
            setTotalDurationSeries([]);
            setDetailRawData([]);
            setDetailGroups([]);
            return undefined;
        }

        let cancelled = false;
        let timer;

        const load = async () => {
            try {
                const currentAxis = buildTimeAxis(tab);

                const [totalData, detailData] = await Promise.all([
                    getEquipmentOpLogsStatsOpStatusDurationQueryString(
                        tab,
                        `${graphParam}&opStatus=ACTIVE`
                    ),
                    getEquipmentMeasurementLogsStatsDurationQueryString(
                        tab,
                        `${graphParam}&code=OP`
                    )
                ]);

                if (cancelled) return;

                setAxis(currentAxis);

                setTotalDurationSeries(
                    mapDurationToAxis(
                        currentAxis,
                        totalData ?? [],
                        tab,
                        t
                    )
                );

                setDetailRawData(
                    Array.isArray(detailData) ? detailData : []
                );
            } catch (error) {
                if (!cancelled) {
                    console.error("Duration query failed:", error);
                }
            }
        };

        load();
        timer = setInterval(load, DELAY_TIME);

        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, [tab, equipmentId, graphParam, t]);

    useEffect(() => {
        let cancelled = false;

        const loadGroups = async () => {
            if (viewType === "TOTAL" || axis.length === 0) {
                setDetailGroups([]);
                return;
            }

            try {
                const groups = await buildDetailDurationGroups(
                    detailRawData,
                    axis,
                    tab,
                    viewType
                );

                if (!cancelled) {
                    setDetailGroups(groups);
                }
            } catch (error) {
                if (!cancelled) {
                    console.error("Detail duration grouping failed:", error);
                }
            }
        };

        loadGroups();

        return () => {
            cancelled = true;
        };
    }, [detailRawData, axis, tab, viewType]);

    const chartSeries = viewType === "TOTAL"
        ? totalDurationSeries
        : buildDetailDurationSeries(
            detailGroups,
            tab,
            selectedItemKey
        );
    
    const baseChartOption = buildOption(
        axis,
        chartSeries,
        textColor,
        bgColor,
        tab,
        t,
        "bar"
    );

    // 날짜별 총 운전시간 계산
    const totalMap = {};

    chartSeries.forEach((series) => {
        totalMap[series.name] = (series.data || []).reduce(
            (sum, value) => sum + (Number(value) || 0),
            0
        );
    });

    const chartOption = {
        ...baseChartOption,

        tooltip: {
            ...baseChartOption.tooltip,
            trigger: "axis",

            formatter: (params) => {
                if (!params) return "";

                const items = Array.isArray(params) ? params : [params];

                return items.map((item) => {
                    if (item.value == null) return "";

                    // DAY는 분 단위, MONTH/YEAR는 시간 단위
                    const rawValue = Number(item.value) || 0;

                    const totalMinutes = Math.round(
                        tab === "DAY" ? rawValue : rawValue * 60
                    );

                    const h = Math.floor(totalMinutes / 60);
                    const m = totalMinutes % 60;

                    const duration =
                        h === 0
                            ? `${m}${t("message.common.text.minute")}`
                            : m === 0
                                ? `${h}${t("message.common.text.time")}`
                                : `${h}${t("message.common.text.time")} ${m}${t("message.common.text.minute")}`;

                    return `
                        ${item.marker || ""}
                        ${item.seriesName || ""}
                        : <b>${duration}</b>
                    `;
                }).join("<br/>");
            },
        },
        
        legend: {
            ...baseChartOption.legend,

            formatter: (name) => {
                // TOTAL일 때만 날짜 옆에 총시간 표시
                if (viewType !== "TOTAL") return name;
                
                const rawTotal = totalMap[name];

                if (rawTotal === undefined) return name;

                const totalMinutes =
                    tab === "DAY"
                        ? Math.floor(rawTotal)
                        : Math.floor(rawTotal * 60);

                const h = Math.floor(totalMinutes / 60);
                const m = totalMinutes % 60;

                const duration =
                    h === 0
                        ? `${m}${t("message.common.text.minute")}`
                        : m === 0
                            ? `${h}${t("message.common.text.time")}`
                            : `${h}${t("message.common.text.time")} ${m}${t("message.common.text.minute")}`;

                return `${name} (${duration})`;
            },

            textStyle: { color: textColor },
            backgroundColor: bgColor,
        },

        yAxis: {
            ...baseChartOption.yAxis,
            name: showCurrentPeriod
                ? `${getDurationUnit(tab, t)} (${getCurrentPeriodLabel(tab)})`
                : baseChartOption.yAxis?.name,
        },
    };

    function getCurrentTotalMinutes() {
        const currentSeries = totalDurationSeries[1];

        if (!Array.isArray(currentSeries?.data)) {
            return 0;
        }

        return currentSeries.data.reduce(
            (sum, value) => sum + (Number(value) || 0),
            0
        );
    }

    // function formatMinutes(value) {
    //     return Number(value || 0).toLocaleString("en-US", {
    //         minimumFractionDigits: 2,
    //         maximumFractionDigits: 2
    //     });
    // }
    function formatMinutes(value) {
        const totalMinutes = Math.floor(Number(value) || 0);

        const h = Math.floor(totalMinutes / 60);
        const m = totalMinutes % 60;

        if (h === 0) {
            return `${m}${t("message.common.text.minute")}`;
        }

        if (m === 0) {
            return `${h}${t("message.common.text.time")}`;
        }

        return `${h}${t("message.common.text.time")} ${m}${t("message.common.text.minute")}`;
    }

    //target === "equipmentView"
    const excelTitle = equipment.name.replace(' ' , '_')+'_'+
                    (viewType === "OPERATION"?t("message.common.chip.operating.status")+'_':viewType === "PART"?t("message.common.chip.drive.components")+'_':'')+
                    (tab === "DAY" ? t("message.common.text.daily")
                        : tab === "MONTH" ? t("message.common.text.monthly")
                            : t("message.common.text.years"))+"_"+t("message.common.text.operationDataList");
    let excelTopHeaders = [
        tab === "DAY" ? t("message.common.text.time") : tab === "MONTH" ? t("message.common.text.day") : t("message.common.text.month2")
    ];
    chartSeries.map((series, index) => {
        excelTopHeaders.push(series.name);
    })
    const excelDatas = axis.map((axisValue, index) => {
            let datas = [axisValue];
            chartSeries.map((series, seriesIndex) => datas.push(series.data?.[index] != null
                ? formatMinutes(
                    tab === "DAY"
                        ? series.data[index]
                        : series.data[index] * 60
                )
                : "-"))
            // chartSeries.map((series, seriesIndex) => datas.push(series.data?.[index] ?? "-"))
            return datas;
        }
    )

    return (
        <React.Fragment>
            <div
                className="row"
                style={{
                    marginTop: "15px",
                    width: "100%",
                }}
            >
                <div className="mixed-chart">
                    <div className="alert_phrase">
                        <div
                            style={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginTop: "-10px",
                                marginBottom: "10px",
                                width: "100%",
                                borderBottom:
                                    "1px solid #636363",
                            }}
                        >
                            <div style={{ padding: "0 10px", textAlign: "center" }}>
                                <Chip
                                    label={t(
                                        "message.common.chip.uptime"
                                    )}
                                    color="primary"
                                />
                            </div>

                            <Tabs
                                value={tab}
                                onChange={(event, value) => {
                                    setTab(value);
                                    setSelectedItemKey(null);
                                }}
                                sx={{
                                    alignItems: "center",
                                    ".MuiTabs-flexContainer": {
                                        justifyContent: "center",
                                    },
                                }}
                            >
                                <Tab
                                    label={t(
                                        "message.common.text.daily"
                                    )}
                                    value="DAY"
                                    className="graphTab"
                                    style={{
                                        border:
                                            tab === "DAY"
                                                ? "3px solid #0061d0"
                                                : "1px solid #0061d0",
                                        margin: "unset",
                                        minWidth: "50px",
                                    }}
                                />

                                <Tab
                                    label={t(
                                        "message.common.text.monthly"
                                    )}
                                    value="MONTH"
                                    className="graphTab"
                                    style={{
                                        border:
                                            tab === "MONTH"
                                                ? "3px solid #0061d0"
                                                : "1px solid #0061d0",
                                        margin: "unset",
                                        minWidth: "50px",
                                    }}
                                />

                                <Tab
                                    label={t(
                                        "message.common.text.years"
                                    )}
                                    value="YEAR"
                                    className="graphTab"
                                    style={{
                                        border:
                                            tab === "YEAR"
                                                ? "3px solid #0061d0"
                                                : "1px solid #0061d0",
                                        margin: "unset",
                                        minWidth: "50px",
                                    }}
                                />
                            </Tabs>
                        </div>

                        {isOperation &&
                        <div>
                            <Tabs
                                value={viewType}
                                onChange={(event, value) => {
                                    setViewType(value);
                                    setSelectedItemKey(null);
                                }}
                                sx={{
                                    ".graphTab": {
                                        fontSize: "16px !important",
                                        margin: "5px !important",
                                        minWidth: "50px",
                                        color: "#fff !important",
                                    },
                                }}
                            >
                                <Tab
                                    label={t(
                                        "message.common.total"
                                    )}
                                    value="TOTAL"
                                    className="graphTab"
                                    style={{
                                        border:
                                            viewType === "TOTAL"
                                                ? "3px solid #0061d0"
                                                : "1px solid #0061d0",
                                    }}
                                />

                                <Tab
                                    label={t(
                                        "message.common.chip.operating.status"
                                    )}
                                    value="OPERATION"
                                    className="graphTab"
                                    style={{
                                        border:
                                            viewType === "OPERATION"
                                                ? "3px solid #0061d0"
                                                : "1px solid #0061d0",
                                    }}
                                />

                                <Tab
                                    label={t(
                                        "message.common.chip.drive.components"
                                    )}
                                    value="PART"
                                    className="graphTab"
                                    style={{
                                        border:
                                            viewType === "PART"
                                                ? "3px solid #0061d0"
                                                : "1px solid #0061d0",
                                    }}
                                />
                            </Tabs>
                        </div>
                        }
                        
                        {viewType !== "TOTAL" && (
                        <Box sx={{ mt: 2, mb: 2, width: "100%", overflowX: "auto" }}>
                            <table
                                className="duration-table"
                                style={{
                                    width: "100%",
                                    borderCollapse: "collapse",
                                    textAlign: "center",
                                    fontSize: "13px",
                                    whiteSpace: "nowrap"
                                }}
                            >
                                <thead>
                                    <tr>
                                        <th style={cellStyle}>{viewType==='OPERATION' ? t('message.common.chip.operating.status'):t('message.common.chip.drive.components')}</th>

                                        {detailGroups.map((item) => (
                                            <th key={item.key} style={cellStyle}>
                                                {item.name}
                                            </th>
                                        ))}

                                        <th style={cellStyle}>{t('message.common.total')}</th>
                                    </tr>
                                </thead>

                                <tbody>
                                    <tr>
                                        <td style={cellStyle}>
                                            {getDurationUnit(tab, t)}
                                        </td>

                                        {detailGroups.map((item) => (
                                            <td
                                                key={item.key}
                                                onClick={() => setSelectedItemKey(item.key)}
                                                style={{
                                                    ...cellStyle,
                                                    cursor: "pointer",
                                                    background:
                                                        selectedItemKey === item.key
                                                            ? "rgba(84,112,198,0.2)"
                                                            : "transparent"
                                                }}
                                            >
                                                {formatMinutes(item.currTotal)}
                                            </td>
                                        ))}

                                        <td
                                            onClick={() => setSelectedItemKey(null)}
                                            style={{
                                                ...cellStyle,
                                                cursor: "pointer",
                                                fontWeight: "bold",
                                                background:
                                                    selectedItemKey === null
                                                        ? "rgba(84,112,198,0.2)"
                                                        : "transparent"
                                            }}
                                        >
                                            {formatMinutes(getCurrentTotalMinutes())}
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </Box>
                        )}

                        <Box sx={{ width: "100%", minWidth: 0 }}>
                            <MainCard content={false}>
                                <ECharts
                                    ref={chartRef}
                                    option={chartOption}
                                    notMerge={true}
                                    lazyUpdate={false}
                                    opts={{
                                        renderer: "svg",
                                        height,
                                    }}
                                    style={{
                                        width: "100%",
                                        height: `${height}px`,
                                    }}
                                />
                            </MainCard>
                        </Box>

                        {/* 기존 상세 데이터 표는 TOTAL에서만 표시합니다. */}
                        {target === "equipmentView" &&
                            // viewType === "TOTAL" && 
                        (
                        <div>
                            <div
                                style={{
                                    marginTop: "15px",
                                    width: "100%",
                                    display: "flex",                // 🔹 Flexbox 적용
                                    justifyContent: "space-between",  // 🔹 좌우 양끝 정렬
                                    alignItems: "center",           // 🔹 세로 중앙 정렬
                                }}
                            >
                                {/* 타이틀 영역 */}
                                <div>
                                    📋{" "}
                                    {tab === "DAY"
                                        ? t("message.common.text.daily")
                                        : tab === "MONTH"
                                        ? t("message.common.text.monthly")
                                        : t("message.common.text.years")}{" "}
                                    {t("message.common.text.operationDataList")} {showCurrentPeriod && ` (${getCurrentPeriodLabel(tab)})`}
                                </div>

                                {/* 🔹 우측에 들어갈 엑셀 다운로드 버튼 영역 */}
                                <div className='no-print-page'>
                                    <CommonExcelDownload excelDatas={excelDatas} excelHeader={excelTopHeaders} title={excelTitle} filename={`${excelTitle}.xlsx`} />
                                </div>
                            </div>

                            <table
                                className="duration-table"
                                style={{
                                    width: "100%",
                                    textAlign: "center",
                                    borderCollapse: "collapse",
                                    color: "#bbb",
                                    fontFamily: "sans-serif",
                                    fontSize: "13px",
                                }}
                            >
                                <thead>
                                    <tr
                                        style={{
                                            background: "#222",
                                            color: "#fff",
                                            fontWeight: "bold",
                                            borderBottom:
                                                "2px solid #333",
                                        }}
                                    >
                                        <th
                                            style={{
                                                padding: "10px",
                                                border: "1px solid #333",
                                            }}
                                        >
                                            {tab === "DAY"
                                                ? t(
                                                        "message.common.text.time"
                                                    )
                                                : tab === "MONTH"
                                                    ? t(
                                                        "message.common.text.day"
                                                    )
                                                    : t(
                                                        "message.common.text.month2"
                                                    )}
                                        </th>
                                        
                                        {chartSeries.map((series, index) => (
                                        <th
                                            key={`${series.name}-${index}`}
                                            style={{
                                                padding: "10px",
                                                border: "1px solid #333",
                                            }}
                                        >
                                            {series.name}
                                        </th>
                                        ))}
                                    </tr>
                                </thead>

                                <tbody>
                                    {axis.map(
                                        (axisValue, index) => {
                                            // const currentSeries = chartSeries;
                                            return (
                                                <tr
                                                    key={axisValue}
                                                    style={{
                                                        borderBottom:
                                                            "1px solid #222",
                                                    }}
                                                >
                                                    <td
                                                        style={{
                                                            padding: "8px",
                                                            border: "1px solid #252525",
                                                            fontWeight:
                                                                "bold",
                                                            color: "#fff",
                                                        }}
                                                    >
                                                        {axisValue}
                                                    </td>

                                                    {chartSeries.map((series, seriesIndex) => (
                                                    <td
                                                        key={`${series.name}-${seriesIndex}`}
                                                        style={{
                                                            padding: "8px",
                                                            border: "1px solid #252525",
                                                            fontWeight: "bold",
                                                            color: seriesIndex % 2 === 0
                                                                ? "#91cc75"
                                                                : "#5470c6",
                                                        }}
                                                    >
                                                        {series.data?.[index] != null
                                                        ? formatMinutes(
                                                            tab === "DAY"
                                                                ? series.data[index]
                                                                : series.data[index] * 60
                                                        )
                                                        : "-"}
                                                        {/* {series.data?.[index] ?? "-"} */}
                                                    </td>
                                                    ))}
                                                </tr>
                                            );
                                        }
                                    )}
                                </tbody>
                            </table>
                        </div>
                        )}
                    </div>
                </div>
            </div>
        </React.Fragment>
    );
}

export default EquipmentDurationGraphs;